/**
 * Parses a Starknet transaction error to extract human-readable failure reasons.
 *
 * Starknet errors contain hex-encoded felt strings with decoded annotations like:
 *   (0x617267656e742f... ('argent/multicall-failed'), 0x494e53... ('INSUFFICIENT_0_AMOUNT'), ...)
 *
 * This function extracts those decoded strings and filters out generic wrapper errors
 * to surface the most useful reason to the user.
 */

const GENERIC_ERROR_REASONS = new Set([
  'argent/multicall-failed',
  'ENTRYPOINT_FAILED',
  'TRANSACTION_FAILED',
]);

/**
 * Error reasons that indicate the swap failed due to slippage / price movement.
 * These are on-chain revert reasons from swap contracts (Nostra pool router, AVNU exchange, etc.)
 */
const SLIPPAGE_ERROR_PATTERNS = [
  'Insufficient tokens received',
  'insufficient tokens received',
  'INSUFFICIENT_OUTPUT_AMOUNT',
  'insufficient output amount',
  'E_MIN_RETURN_NOT_REACHED',
  'min return not reached',
];

export function parseTransactionError(error: unknown): string | undefined {
  const message = error instanceof Error ? error.message : String(error);

  // Extract all decoded felt strings from parenthetical annotations: ('reason')
  const decodedReasons: string[] = [];
  const regex = /\('([^']+)'\)/g;
  let match;
  while ((match = regex.exec(message)) !== null) {
    decodedReasons.push(match[1]);
  }

  // Filter out generic wrapper errors to find the actual cause
  const meaningfulReasons = decodedReasons.filter((reason) => !GENERIC_ERROR_REASONS.has(reason));

  if (meaningfulReasons.length > 0) {
    return meaningfulReasons.join(', ');
  }

  // If no decoded reasons found, try to extract a short message from the error
  if (decodedReasons.length > 0) {
    return decodedReasons.join(', ');
  }

  // Fallback: check for common error patterns
  if (message.includes('Transaction execution has failed')) {
    return 'Transaction execution failed';
  }

  return undefined;
}

/**
 * Checks if the error is caused by slippage / price movement during the swap.
 * This happens when the actual output tokens are less than the minimum specified,
 * typically because the price moved between when the quote was fetched and when
 * the transaction was simulated/executed.
 */
export function isSlippageError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);

  return SLIPPAGE_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}
